import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, useScrollTo } from "@/context";

const LINKS = ["home", "services", "experience", "about", "fees", "contact"];

export const Wordmark = ({ dark = false }) => (
  <img
    src="/brand/logo-mark.png"
    alt="MH — Merce Homar"
    className="h-11 sm:h-12 w-auto select-none"
  />
);

export default function Nav() {
  const { lang, setLang, t } = useLang();
  const scrollTo = useScrollTo();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const go = (id) => {
    setOpen(false);
    if (id === "experience" || id === "about" || id === "fees" || id === "contact") {
      navigate(`/${id}`);
    } else if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      scrollTo(`#${id}`);
    }
  };

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-ivory/85 backdrop-blur-xl border-b border-taupe/20">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 h-20 flex items-center justify-between">
          <button
            data-testid="nav-wordmark"
            onClick={() => go("home")}
            className="text-left"
            aria-label="Merce Homar — home"
          >
            <Wordmark />
          </button>

          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
            {LINKS.map((id) => (
              <button
                key={id}
                data-testid={`nav-link-${id}`}
                onClick={() => go(id)}
                className="group relative text-[11px] uppercase tracking-[0.24em] text-charcoal/80 hover:text-charcoal transition-colors duration-300"
              >
                {t.nav[id]}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-6">
            <div className="flex items-center gap-2 text-[11px] tracking-[0.2em]">
              <button
                data-testid="lang-toggle-en"
                onClick={() => setLang("en")}
                className={lang === "en" ? "text-gold" : "text-taupe hover:text-charcoal transition-colors"}
              >
                EN
              </button>
              <span className="text-taupe/50">|</span>
              <button
                data-testid="lang-toggle-es"
                onClick={() => setLang("es")}
                className={lang === "es" ? "text-gold" : "text-taupe hover:text-charcoal transition-colors"}
              >
                ES
              </button>
            </div>
            <button
              data-testid="nav-cta-work-with-me"
              onClick={() => go("contact")}
              className="border border-charcoal px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] text-charcoal hover:border-gold hover:text-gold transition-colors duration-300"
            >
              {t.nav.cta}
            </button>
          </div>

          <button
            data-testid="mobile-menu-button"
            className="lg:hidden text-charcoal"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-ivory flex flex-col justify-center px-8 lg:hidden"
          >
            <nav className="space-y-6">
              {LINKS.map((id, i) => (
                <motion.button
                  key={id}
                  data-testid={`mobile-nav-link-${id}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                  onClick={() => go(id)}
                  className="block font-serif text-4xl text-charcoal text-left"
                >
                  {t.nav[id]}
                </motion.button>
              ))}
            </nav>
            <div className="mt-12 flex items-center gap-3 text-xs tracking-[0.2em]">
              <button
                data-testid="mobile-lang-toggle-en"
                onClick={() => setLang("en")}
                className={lang === "en" ? "text-gold" : "text-taupe"}
              >
                EN
              </button>
              <span className="text-taupe/50">|</span>
              <button
                data-testid="mobile-lang-toggle-es"
                onClick={() => setLang("es")}
                className={lang === "es" ? "text-gold" : "text-taupe"}
              >
                ES
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-ivory/90 backdrop-blur-lg border-t border-taupe/20 p-3">
        <button
          data-testid="mobile-sticky-cta"
          onClick={() => go("contact")}
          className="w-full bg-charcoal text-ivory py-3.5 text-[11px] uppercase tracking-[0.24em] hover:bg-gold hover:text-charcoal transition-colors duration-300"
        >
          {t.nav.cta}
        </button>
      </div>
    </>
  );
}
